import type { SxProps, Theme } from "@mui/material/styles";
import type {
  PdfJobResultState,
  PdfJobStatus,
} from "features/pdfTools/pdfTools";
import type { AdminStorageFolderListing } from "types/interfaces/adminUsers";

export const PDF_JOB_STATUS_LABELS: Record<PdfJobStatus, string> = {
  pending: "Pending",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  stopped: "Stopped",
  canceled: "Stopped",
};

export const PDF_JOB_STATUS_CHIP_SX: Record<PdfJobStatus, SxProps<Theme>> = {
  pending: { bgcolor: "rgba(255,152,0,0.12)", color: "warning.dark" },
  running: { bgcolor: "rgba(2,136,209,0.12)", color: "info.dark" },
  completed: { bgcolor: "rgba(46,125,50,0.12)", color: "success.dark" },
  failed: { bgcolor: "rgba(211,47,47,0.12)", color: "error.dark" },
  stopped: { bgcolor: "rgba(0,0,0,0.08)", color: "text.secondary" },
  canceled: { bgcolor: "rgba(0,0,0,0.08)", color: "text.secondary" },
};

export const PDF_JOB_RESULT_LABELS: Record<
  Exclude<PdfJobResultState, "">,
  string
> = {
  in_review: "In review",
  saving: "Saving…",
  saved: "Saved",
  discarded: "Discarded",
};

export const PDF_JOB_PHASE_LABELS: Record<string, string> = {
  downloading: "Downloading files",
  extracting: "Extracting PDFs",
  splitting: "Splitting & naming",
  uploading: "Uploading results",
  zipping: "Building archive",
};

export function formatJobDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

export function defaultSaveSubpath(splitterKey: string): string {
  // "pdf.paylocity.w2" -> "paylocity/data/W2_splitted"
  const parts = splitterKey.split(".");
  const provider = parts[1] || "splitter";
  const document = (parts[2] || parts[0] || "result").toUpperCase();
  return `${provider}/data/${document}_splitted`;
}

export interface SaveDestinationFolder {
  id: number;
  label: string;
  prefix: string;
}

// Only the roots level of the listing carries a UserFolder id, which is what
// the save endpoint takes.
export function saveDestinationFolders(
  listing: AdminStorageFolderListing | undefined,
): SaveDestinationFolder[] {
  return (listing?.items ?? []).flatMap((item) =>
    item.id == null
      ? []
      : [
          {
            id: item.id,
            label: item.display_name || item.prefix,
            prefix: item.prefix,
          },
        ],
  );
}
